import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { getApp } from "../app";
import { getOpenedCasesFromMpn, getTopLevelLength } from "../api.service";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import { Part } from "../../../../shared/models/part";
import { Row } from "../tree/tree.util";
import { Subscription } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { TreeMode } from "../RM/RM.service";
import { Customer } from "../../../../shared/types/customers";
import { RmSection } from "../rm-submenu/rm-submenu.service.type";

@Component({
    selector: "app-tree",
    templateUrl: "./tree.component.html",
    styleUrls: ["./tree.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TreeComponent implements OnInit, OnDestroy {
  readonly treeRowHeight = 42;
  private readonly treeRowOverscan = 10;
  treeStartIndex = 0;
  treeEndIndex = 50;

  columns: string[] = [];
  parts: Part[] = [];
  manufacturers: Manufacturer[] = [];

  private _routeSubscription: Subscription = new Subscription();
  private _sectionSubscription: Subscription = new Subscription();
  private _routeParamsSubscription: Subscription = new Subscription();
  structureRows: Row[] = [];
  topLevelItems: Partial<Part>[] = [];
  loading = false;
  customExportEnabled = false;
  proactiveItemsEnabled = false;
  engineeringEnabled = false;
  /** true for manufacturer-based views (where the manufacturer exports apply) */
  isManufacturerView = false;
  treeSections: RmSection[] = [
    "status",
    "risk",
    "likelihood",
    "impact",
    "rohs",
    "reach",
    "lead",
  ];

  app = getApp((app) => {
    this.app = app;
  });

  constructor(private route: ActivatedRoute) {}

  get visibleTreeStartIndex() {
    return Math.min(
      this.treeStartIndex,
      Math.max(0, this.app.tree.items.length - 1)
    );
  }

  get visibleTreeEndIndex() {
    return Math.min(
      this.app.tree.items.length,
      Math.max(this.treeEndIndex, this.visibleTreeStartIndex + 1)
    );
  }

  get visibleTreeItems() {
    return this.app.tree.items.slice(
      this.visibleTreeStartIndex,
      this.visibleTreeEndIndex
    );
  }

  get treeTopSpacerHeight() {
    return this.visibleTreeStartIndex * this.treeRowHeight;
  }

  get treeBottomSpacerHeight() {
    return (
      (this.app.tree.items.length - this.visibleTreeEndIndex) *
      this.treeRowHeight
    );
  }

  onTreeScroll(event: Event) {
    const element = event.target as HTMLElement;
    const startIndex = Math.max(
      0,
      Math.floor(element.scrollTop / this.treeRowHeight) -
        this.treeRowOverscan
    );
    const visibleRows =
      Math.ceil(element.clientHeight / this.treeRowHeight) +
      this.treeRowOverscan * 2;

    this.treeStartIndex = startIndex;
    this.treeEndIndex = Math.min(
      this.app.tree.items.length,
      startIndex + visibleRows
    );
  }

  getTreeRowIndex(index: number) {
    return this.visibleTreeStartIndex + index;
  }

  trackTreeRow(_index: number, row: Row) {
    return row;
  }

  async ngOnInit() {
    this.app.type = "part";
    this.app.RM.mode = "tree";
    this.app.RM.isTree = true;
    this.app.RM.isPart = false;
    this.app.RM.isManufacturer = false;
    this.app.tree.currentColumns = this.app.tree.getTreeColumns();

    this.checkProactiveItemsView();

    this._routeSubscription = this.route.data.subscribe((data) => {
      this.app.RM.displayRMHeader = data.displayRMHeader;
      this.app.routing.setTabTitle(data.title);
    });

    this._routeParamsSubscription = this.route.params.subscribe(
      async (value) => {
        const assemblyParameter = value.assembly;

        if (assemblyParameter == null || assemblyParameter === "") {
          this.app.RM.fromRMSearch = false;
          this.app.filterTree.assemblySelected = "";
          this.app.filterTree.resetFilterTree();
          this.app.paginator.pageSize = 25;
          await this.app.tree.getItems();
          return;
        }

        this.loading = true;
        // Reset any remaining filters before applying the assembly filter
        this.app.RM.fromRMSearch = false;
        this.app.filterTree.assemblySelected = "";
        this.app.filterTree.resetFilterTree();

        const assembly = decodeURIComponent(assemblyParameter);
        this.app.filterTree.assemblySelected = assembly;
        this.app.tree.loadingTreeRows = true;
        this.app.spinner.showSpinner("tree");
        await this.app.filterTree.filter("part.partNumber", assembly);
        this.app.RM.fromRMSearch = true;
        this.app.routing.setTabTitle(assembly + " - LCM Client");
        this.checkCustomExportState();
        this.loading = false;
        this.app.tree.loadingTreeRows = false;
        this.app.spinner.hideSpinner("tree");
      }
    );

    this._sectionSubscription = this.app.RMSubmenu.sectionSubject.subscribe(
      async () => {
        // if (this.app.tree.items.length > 0) {
        //   this.app.tree.loadingTreeRows = true;
        // }
        this.app.tree.currentColumns = this.app.tree.getTreeColumns();
      }
    );
    /** get the list of cases that contain at least a cpn or a mpn */
    this.app.thread.threads = await getOpenedCasesFromMpn(
      this.app.customers.expectCurrent
    );
  }

  checkCustomExportState() {
    const customersWithCustomExport = [Customer.AUTOFLUG];

    this.customExportEnabled = customersWithCustomExport.includes(
      this.app.customers.expectCurrent
    );

    this.checkProactiveItemsView();
  }

  /** Enable the "Proactive Items" and "Engineering" top-level views for RAD only */
  checkProactiveItemsView() {
    const isRAD =
      this.app.customers.expectCurrent === Customer.RHEINMETALLAIRDEFENCE;
    this.proactiveItemsEnabled = isRAD;
    this.engineeringEnabled = isRAD;
  }

  displayMessage(row: any) {
    if (this.app.tree.isManufacturerRow(row)) {
      if (this.app.manufacturer.hasManufacturers(row)) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  switchView(view: TreeMode) {
    this.app.RM.mode = view;
    this.app.RM.filterOpen = false;
    this.isManufacturerView = !["tree", "proactiveItems", "engineering"].includes(
      view
    );
    if (view === "proactiveItems" || view === "engineering") {
      // the embedded filter-table component loads the assembly data on init
      return;
    }
    if (this.app.RM.mode !== "tree") {
      this.app.manufacturersFilter.getFilterResultBasedOnView("1");
      this.app.filterHeaderTable.resetAllFilters.next(true);
    } else {
      this.app.filterTree.getTreeSearchResult();
      this.app.type = "part";
    }
  }

  ngOnDestroy(): void {
    if (this._routeSubscription) {
      this._routeSubscription.unsubscribe();
    }
    if (this._sectionSubscription) {
      this._sectionSubscription.unsubscribe();
    }
    if (this._routeParamsSubscription) {
      this._routeParamsSubscription.unsubscribe();
    }
  }
}
